import { OrigemNFeEntradaEnum } from '../enums/repo-dfe/origem-nfe-entrada.enum';
import { TipoEventoNFeEnum } from '../enums/dfe/tipo-evento-nfe.enum';
import { StatusNFeEntradaEnum } from '../enums/repo-dfe/status-nfe-entrada.enum';
import { DisponibilidadeDocumentoSefazEnum } from '../enums/dfe/disponibilidade-documento-sefaz.enum';
import { PedidoCompraVinculadoNFeEntradaStruct } from '../services/structs/orquestrador-compras/pedido-compra-vinculado-nfe-entrada.struct';

/**
 * Traduz o status do DFe de entrada na classe global de badge correspondente.
 * As cores acompanham os cartões da Central: Capturada (azul), Em lançamento (amarelo)
 * e Lançada (verde), usando somente
 * as classes já existentes no styles.css - nada de cor nova em CSS de componente.
 */
export function classeBadgeStatusNFeEntrada(idStatusNFeEntrada: number | null | undefined): string {
  switch (idStatusNFeEntrada) {
    case StatusNFeEntradaEnum.CapturadaSefaz: return 'badge-azul';
    case StatusNFeEntradaEnum.EmLancamento: return 'badge-amarelo';
    case StatusNFeEntradaEnum.Lancada: return 'badge-verde';
    default: return 'badge-cinza';
  }
}

/**
 * Rótulo do status, para telas que não recebem o lookup `listaStatus` da listagem da Central —
 * caso da Gaveta de NFs. Mesma abordagem do `rotuloManifestacao` logo abaixo.
 */
export function rotuloStatusNFeEntrada(idStatusNFeEntrada: number | null | undefined): string {
  switch (idStatusNFeEntrada) {
    case StatusNFeEntradaEnum.CapturadaSefaz: return 'Capturada SEFAZ';
    case StatusNFeEntradaEnum.EmLancamento: return 'Em lançamento';
    case StatusNFeEntradaEnum.Lancada: return 'Lançada';
    default: return '—';
  }
}

/** Nota capturada cujo XML completo ainda não chegou: só o resumo está disponível, sem itens nem impostos. */
export function isStatusParcial(
  idStatusNFeEntrada: number | null | undefined,
  idDisponibilidadeDocumentoSefaz: DisponibilidadeDocumentoSefazEnum | null | undefined
): boolean {
  return idStatusNFeEntrada === StatusNFeEntradaEnum.CapturadaSefaz
    && idDisponibilidadeDocumentoSefaz !== DisponibilidadeDocumentoSefazEnum.DocumentoCompleto;
}

export function iconeStatusNFeEntrada(idStatusNFeEntrada: number | null | undefined): string {
  switch (idStatusNFeEntrada) {
    case StatusNFeEntradaEnum.CapturadaSefaz: return 'cloud_download';
    case StatusNFeEntradaEnum.EmLancamento: return 'pending_actions';
    case StatusNFeEntradaEnum.Lancada: return 'check_circle';
    default: return 'help';
  }
}

export function rotuloDisponibilidadeDocumentoSefaz(idDisponibilidadeDocumentoSefaz: DisponibilidadeDocumentoSefazEnum | null | undefined): string {
  switch (idDisponibilidadeDocumentoSefaz) {
    case DisponibilidadeDocumentoSefazEnum.Identificado: return 'Identificado';
    case DisponibilidadeDocumentoSefazEnum.ResumoRecebido: return 'Resumo recebido';
    case DisponibilidadeDocumentoSefazEnum.DocumentoCompleto: return 'Documento completo';
    default: return '';
  }
}

export function descricaoDisponibilidadeDocumentoSefaz(idDisponibilidadeDocumentoSefaz: DisponibilidadeDocumentoSefazEnum | null | undefined): string {
  switch (idDisponibilidadeDocumentoSefaz) {
    case DisponibilidadeDocumentoSefazEnum.Identificado: return 'Existe apenas a chave de acesso, normalmente por evento recebido antes do documento.';
    case DisponibilidadeDocumentoSefazEnum.ResumoRecebido: return 'Existe resumo de distribuição, sem XML completo do documento.';
    case DisponibilidadeDocumentoSefazEnum.DocumentoCompleto: return 'Existe XML completo do documento recebido.';
    default: return '';
  }
}

/** Ícone da origem da nota, conforme o dicionário de origem da Story (seção 0.2). */
export function iconeOrigemNFeEntrada(idOrigemNFeEntrada: number | null | undefined): string {
  switch (idOrigemNFeEntrada) {
    case OrigemNFeEntradaEnum.Sefaz: return 'sync';
    case OrigemNFeEntradaEnum.ImportacaoXml: return 'folder_open';
    case OrigemNFeEntradaEnum.LancamentoManual: return 'edit_note';
    default: return 'help';
  }
}

/**
 * Classe de badge da situação da manifestação SEFAZ, pelo código do evento.
 * As cores acompanham o dicionário da Story (seção 0.3).
 */
export function classeBadgeManifestacao(idTipoEventoManifestacaoAtual: number | null | undefined): string {
  switch (idTipoEventoManifestacaoAtual) {
    case TipoEventoNFeEnum.CienciaEmissao: return 'badge-azul';
    case TipoEventoNFeEnum.ConfirmacaoOperacao: return 'badge-verde';
    case TipoEventoNFeEnum.DesconhecimentoOperacao: return 'badge-vermelho';
    case TipoEventoNFeEnum.OperacaoNaoRealizada: return 'badge-vermelho';
    default: return 'badge-amarelo';
  }
}

/** Rótulo da situação da manifestação SEFAZ. */
export function rotuloManifestacao(idTipoEventoManifestacaoAtual: number | null | undefined): string {
  switch (idTipoEventoManifestacaoAtual) {
    case TipoEventoNFeEnum.CienciaEmissao: return 'Ciência';
    case TipoEventoNFeEnum.ConfirmacaoOperacao: return 'Confirmada';
    case TipoEventoNFeEnum.DesconhecimentoOperacao: return 'Desconhecida';
    case TipoEventoNFeEnum.OperacaoNaoRealizada: return 'Não realizada';
    default: return 'Pendente';
  }
}

/** Chave da etapa lateral "divergencia" do Kanban de Compras (API Compras, etapa_kanban.chave). */
const CHAVE_ETAPA_PEDIDO_DIVERGENCIA = 'divergencia';

/**
 * Pedidos vinculados à nota que estão na etapa de divergência do processo de compras. Indicador
 * só de leitura: não entra na contagem nem no bloqueio de divergências da própria nota.
 */
export function pedidosComDivergencia(pedidos: PedidoCompraVinculadoNFeEntradaStruct[] | null | undefined): PedidoCompraVinculadoNFeEntradaStruct[] {
  return (pedidos ?? []).filter(x => x.chaveEtapa === CHAVE_ETAPA_PEDIDO_DIVERGENCIA);
}

export function tooltipPedidosComDivergencia(pedidos: PedidoCompraVinculadoNFeEntradaStruct[]): string {
  return pedidos.length === 1
    ? `O pedido ${pedidos[0].codigoCompra} está em divergência no processo de compras.`
    : `Os pedidos ${pedidos.map(x => x.codigoCompra).join(', ')} estão em divergência no processo de compras.`;
}
